// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/architecture/python-fastapi-architecture.md

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  Chip,
  TableSortLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Stack,
  CircularProgress,
  OutlinedInput,
  Checkbox,
  ListItemText,
  InputAdornment,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  Refresh,
  FilterList,
  Visibility,
  Delete,
  Stop,
  CalendarToday,
  ContentCopy,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { apiClient, API_ENDPOINTS } from '../config/api';
import VoiceDetailModal from './VoiceDetailModal';
import TaskLogViewer from './TaskLogViewer';
import {
  isLikelyVoiceTaskId,
  buildVoiceProcessTaskDeepLink,
} from '../utils/voiceProcessTaskDeepLink';
import { formatVoiceTaskDisplayDate } from '../utils/formatBangkokDate';

const ProcessedFilesTable = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const taskFromUrl = searchParams.get('task');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFiles, setTotalFiles] = useState(0);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState(null);
  
  // Log viewer modal
  const [logViewerOpen, setLogViewerOpen] = useState(false);
  const [logTaskId, setLogTaskId] = useState(null);
  const [logTaskName, setLogTaskName] = useState(null);
  const [copySnackbar, setCopySnackbar] = useState({ open: false, message: '' });
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState([]); // Multi-select array
  const [languageFilter, setLanguageFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  
  // Teams data
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);

  const rowsPerPage = 20;

  // Open detail modal from ?task=<uuid> (Copy Link for teammates)
  useEffect(() => {
    if (!taskFromUrl || !isLikelyVoiceTaskId(taskFromUrl)) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(
          `${API_ENDPOINTS.VOICE_TASKS}/${encodeURIComponent(taskFromUrl)}`
        );
        if (cancelled) return;
        if (res.data?.success && res.data?.task?.id) {
          setSelectedFileId(res.data.task.id);
          setSelectedFileName(res.data.task.file_name || res.data.task.id);
          setDetailModalOpen(true);
        }
      } catch {
        if (cancelled) return;
        setSelectedFileId(taskFromUrl);
        setSelectedFileName('Transcription');
        setDetailModalOpen(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [taskFromUrl]);

  /** Label for boolean QA result column (e.g. Qualify 4/4, Not Qualify 3/4). */
  const getBooleanResultLabel = (file) => {
    if (file.qualified == null) return null;
    const passed = file.boolean_passed_count;
    const total = file.boolean_total_count;
    const prefix = file.qualified ? 'Qualify' : 'Not Qualify';
    if (
      typeof passed === 'number' &&
      typeof total === 'number' &&
      total > 0
    ) {
      return `${prefix} ${passed}/${total}`;
    }
    return file.qualified ? 'Qualified' : 'Not Qualified';
  };

  // Fetch processed files data
  const fetchFiles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: rowsPerPage.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      if (statusFilter) params.append('status', statusFilter);
      
      // Multi-select teams: send as multiple team_filter params
      if (teamFilter && teamFilter.length > 0) {
        teamFilter.forEach(teamId => params.append('team_filter', teamId));
      }
      
      if (languageFilter) params.append('language_filter', languageFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      // Date range filter
      if (dateFrom) params.append('date_from', dayjs(dateFrom).format('YYYY-MM-DD'));
      if (dateTo) params.append('date_to', dayjs(dateTo).format('YYYY-MM-DD'));

      const response = await apiClient.get(`${API_ENDPOINTS.VOICE_PROCESSED_FILES}?${params}`);
      const data = response.data;

      // Ensure we have valid data structure
      const tasks = Array.isArray(data.tasks) ? data.tasks : [];
      const total = typeof data.total === 'number' ? data.total : 0;

      setFiles(tasks);
      setTotalFiles(total);
      setTotalPages(Math.ceil(total / rowsPerPage));
    } catch (error) {
      console.error('Failed to fetch processed files:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle sort
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Fetch teams for filtering (only user's teams)
  const fetchTeams = async () => {
    setLoadingTeams(true);
    try {
      const response = await apiClient.get(API_ENDPOINTS.TEAMS_FOR_FILTERING);
      setTeams(response.data.teams || []);
    } catch (error) {
      console.error('Failed to fetch teams for filtering:', error);
      setTeams([]);
    } finally {
      setLoadingTeams(false);
    }
  };

  // Handle filter change
  const handleFilterChange = (filterType, value) => {
    setPage(1); // Reset to first page when filtering
    switch (filterType) {
      case 'status':
        setStatusFilter(value);
        break;
      case 'team':
        setTeamFilter(value);
        break;
      case 'language':
        setLanguageFilter(value);
        break;
      case 'search':
        setSearchTerm(value);
        break;
      default:
        break;
    }
  };

  // Handle clear all filters
  const handleClearAllFilters = () => {
    setPage(1);
    setStatusFilter('');
    setTeamFilter([]);
    setLanguageFilter('');
    setSearchTerm('');
    setDateFrom(null);
    setDateTo(null);
  };

      // Handle row click
      const handleRowClick = (file) => {
        // Open log viewer to see real-time status
        setLogTaskId(file.id);
        setLogTaskName(file.file_name);
        setLogViewerOpen(true);
      };
      
      const handleViewDetails = (file, event) => {
        // Stop propagation to prevent row click
        event.stopPropagation();
        setSelectedFileId(file.id);
        setSelectedFileName(file.file_name);
        setDetailModalOpen(true);
      };

  const handleCopyTaskDeepLink = async (event, taskId) => {
    event.stopPropagation();
    const url = buildVoiceProcessTaskDeepLink(taskId);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopySnackbar({
        open: true,
        message:
          'Link copied. Signed-in teammates with access can open Voice Process with this task.',
      });
    } catch {
      setCopySnackbar({
        open: true,
        message: 'Could not copy to clipboard. Check browser permissions.',
      });
    }
  };

  // Handle action button click
  const handleActionClick = async (fileId, status, action) => {
    try {
      switch (action) {
        case 'delete':
          await apiClient.delete(`${API_ENDPOINTS.VOICE_DELETE}/${fileId}`);
          break;
        case 'cancel':
          await apiClient.patch(`${API_ENDPOINTS.VOICE_CANCEL}/${fileId}/cancel`);
          break;
            case 'view':
              // Find the file to get both id and name
              const file = files.find(f => f.id === fileId);
              if (file) {
                handleViewDetails(file, { stopPropagation: () => {} });
              }
              break;
        default:
          break;
      }
      // Refresh data after action
      fetchFiles();
    } catch (error) {
      console.error(`Failed to ${action} file:`, error);
    }
  };

  // Get action button based on status
  const getActionButton = (file) => {
    if (!file) return null;
    const { id: file_id, status } = file;
    
    switch (status) {
      case 'queued':
        return (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleActionClick(file_id, status, 'delete');
            }}
            color="error"
            title="Remove from queue"
          >
            <Delete />
          </IconButton>
        );
      case 'processing':
      case 'analysing':
        return (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleActionClick(file_id, status, 'cancel');
            }}
            color="warning"
            title="Cancel processing"
          >
            <Stop />
          </IconButton>
        );
      case 'ready_to_analyse':
      case 'completed':
        return (
          <IconButton
            size="small"
            onClick={(e) => handleViewDetails(file, e)}
            color="primary"
            title="View transcription & analysis"
          >
            <Visibility />
          </IconButton>
        );
      case 'failed':
        return (
          <IconButton
            size="small"
            onClick={(e) => handleViewDetails(file, e)}
            color="default"
            title="View error details"
          >
            <Visibility />
          </IconButton>
        );
      case 'cancelled':
        return (
          <IconButton
            size="small"
            onClick={(e) => handleViewDetails(file, e)}
            color="default"
            title="View details"
          >
            <Visibility />
          </IconButton>
        );
      default:
        return null;
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'processing':
        return 'primary';
      case 'ready_to_analyse':
        return 'info';
      case 'analysing':
        return 'secondary';
      case 'queued':
        return 'warning';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  // Prefer API team_name (works for admins without team membership); else filter list.
  const getTeamName = (file) => {
    if (file.team_name) return file.team_name;
    const teamId = file.team_id;
    if (!teamId) return 'None';
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : teamId;
  };

  // Get language name
  const getLanguageName = (languageCode) => {
    if (!languageCode) return 'Unknown';
    const languageMap = {
      'en': 'English',
      'th': 'Thai',
      'zh': 'Chinese'
    };
    return languageMap[languageCode] || languageCode.toUpperCase();
  };

  // Load teams on mount
  useEffect(() => {
    fetchTeams();
  }, []);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchFiles();
  }, [page, sortBy, sortOrder, statusFilter, teamFilter, languageFilter, searchTerm, dateFrom, dateTo]);

  return (
    <Box sx={{ mt: 3 }}>
      {/* Table Header with Refresh Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Processed Files ({totalFiles})
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchFiles}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            placeholder="Search by file name..."
            value={searchTerm}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            sx={{ minWidth: 180, fontSize: '0.875rem' }}
          />
          
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel sx={{ fontSize: '0.875rem' }}>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              label="Status"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="" sx={{ fontSize: '0.875rem' }}>All</MenuItem>
              <MenuItem value="queued" sx={{ fontSize: '0.875rem' }}>Queued</MenuItem>
              <MenuItem value="processing" sx={{ fontSize: '0.875rem' }}>Processing</MenuItem>
              <MenuItem value="ready_to_analyse" sx={{ fontSize: '0.875rem' }}>Ready</MenuItem>
              <MenuItem value="analysing" sx={{ fontSize: '0.875rem' }}>Analysing</MenuItem>
              <MenuItem value="completed" sx={{ fontSize: '0.875rem' }}>Completed</MenuItem>
              <MenuItem value="failed" sx={{ fontSize: '0.875rem' }}>Failed</MenuItem>
              <MenuItem value="cancelled" sx={{ fontSize: '0.875rem' }}>Cancelled</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel sx={{ fontSize: '0.875rem' }}>Team</InputLabel>
            <Select
              multiple
              value={teamFilter}
              onChange={(e) => {
                setTeamFilter(e.target.value);
                setPage(1);
              }}
              input={<OutlinedInput label="Team" />}
              renderValue={(selected) => {
                if (selected.length === 0) return 'All Teams';
                if (selected.length === 1) {
                  const team = teams.find(t => t.id === selected[0]);
                  return team ? team.name : 'Selected';
                }
                return `${selected.length} teams selected`;
              }}
              sx={{ fontSize: '0.875rem' }}
              disabled={loadingTeams}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                  },
                },
              }}
            >
              {teams.map((team) => (
                <MenuItem key={team.id} value={team.id} sx={{ fontSize: '0.875rem' }}>
                  <Checkbox checked={teamFilter.indexOf(team.id) > -1} />
                  <ListItemText primary={team.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel sx={{ fontSize: '0.875rem' }}>Language</InputLabel>
            <Select
              value={languageFilter}
              onChange={(e) => handleFilterChange('language', e.target.value)}
              label="Language"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="" sx={{ fontSize: '0.875rem' }}>All</MenuItem>
              <MenuItem value="en" sx={{ fontSize: '0.875rem' }}>English</MenuItem>
              <MenuItem value="th" sx={{ fontSize: '0.875rem' }}>Thai</MenuItem>
              <MenuItem value="zh" sx={{ fontSize: '0.875rem' }}>Chinese</MenuItem>
              <MenuItem value="other" sx={{ fontSize: '0.875rem' }}>Other</MenuItem>
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <DatePicker
                label="From"
                value={dateFrom}
                onChange={(newValue) => {
                  setDateFrom(newValue);
                  setPage(1);
                }}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 130, fontSize: '0.875rem' }
                  }
                }}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>-</Typography>
              <DatePicker
                label="To"
                value={dateTo}
                onChange={(newValue) => {
                  setDateTo(newValue);
                  setPage(1);
                }}
                format="DD/MM/YYYY"
                minDate={dateFrom}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 130, fontSize: '0.875rem' }
                  }
                }}
              />
              {(dateFrom || dateTo) && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setDateFrom(null);
                    setDateTo(null);
                    setPage(1);
                  }}
                  sx={{ ml: -0.5 }}
                  title="Clear dates"
                >
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </Box>
          </LocalizationProvider>
          
          {/* Clear All Button */}
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearAllFilters}
            sx={{ 
              fontSize: '0.875rem',
              minWidth: 100,
              borderColor: 'grey.400',
              color: 'grey.600',
              '&:hover': {
                borderColor: 'grey.600',
                backgroundColor: 'grey.50'
              }
            }}
          >
            Clear All
          </Button>
        </Stack>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'file_name'}
                  direction={sortBy === 'file_name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('file_name')}
                >
                  File ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'team_id'}
                  direction={sortBy === 'team_id' ? sortOrder : 'asc'}
                  onClick={() => handleSort('team_id')}
                >
                  Team
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'language'}
                  direction={sortBy === 'language' ? sortOrder : 'asc'}
                  onClick={() => handleSort('language')}
                >
                  Language
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'status'}
                  direction={sortBy === 'status' ? sortOrder : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>Result</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'created_at'}
                  direction={sortBy === 'created_at' ? sortOrder : 'asc'}
                  onClick={() => handleSort('created_at')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">Link</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : files.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No files found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              files.map((file) => (
                <TableRow 
                  key={file.id} 
                  hover 
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleRowClick(file)}
                >
                  <TableCell>
                    {file.file_count && file.file_count > 1 ? (
                      <Box>
                        <Typography variant="body2">
                          {file.file_name || file.id || 'Unknown'}
                        </Typography>
                        <Chip 
                          label={`${file.file_count} files`} 
                          size="small" 
                          color="primary" 
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    ) : (
                      <Typography variant="body2">
                        {file.file_name || file.id || 'Unknown'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {getTeamName(file)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {getLanguageName(file.original_language)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={file.status} 
                      color={getStatusColor(file.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {file.criteria_type === 'boolean' ? (
                      file.qualified != null ? (
                        <Chip
                          label={getBooleanResultLabel(file)}
                          color={file.qualified ? 'success' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )
                    ) : file.qa_score != null ? (
                      <Typography variant="body2">{Math.round(Number(file.qa_score))}/100</Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatVoiceTaskDisplayDate(file)}
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Tooltip title="Copy Voice Process link for teammates (signed-in, with access)">
                      <IconButton
                        size="small"
                        color="primary"
                        aria-label="Copy Voice Process link for this task"
                        onClick={(e) => handleCopyTaskDeepLink(e, file.id)}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {getActionButton(file)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
      
      {/* Voice Detail Modal */}
      <VoiceDetailModal
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedFileId(null);
          setSelectedFileName(null);
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete('task');
              return next;
            },
            { replace: true }
          );
        }}
        onListRefreshSuggested={fetchFiles}
        fileId={selectedFileId}
        fileName={selectedFileName}
      />
      
      {/* Task Log Viewer Modal */}
      <TaskLogViewer
        open={logViewerOpen}
        onClose={() => {
          setLogViewerOpen(false);
          setLogTaskId(null);
          setLogTaskName(null);
          fetchFiles(); // Refresh table when closing log viewer
        }}
        taskId={logTaskId}
        fileName={logTaskName}
        onTaskDeleted={(deletedTaskId) => {
          // Refresh the files list when a task is deleted
          fetchFiles();
          console.log(`Task ${deletedTaskId} deleted successfully`);
        }}
      />

      <Snackbar
        open={copySnackbar.open}
        autoHideDuration={5000}
        onClose={() => setCopySnackbar((s) => ({ ...s, open: false }))}
        message={copySnackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default ProcessedFilesTable;
